import React from "react";
import { Route, Redirect } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "./ErrorFallback";
import { useGlobalConfig } from "../GlobalConfigContext";
import { deconnexionEnCours } from "../utils/logoutBroadcast";

function CenteredLoader({ message = "Authentification en cours..." }) {
  return (
    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
      {message}<br />
      Au bout de 5 secondes, si cette page ne s'est pas actualisée, veuillez vous reconnecter.
    </div>
  );
}

export default function PrivateRoute({
  privateComponent: Component,
  userInfo,
  rolesAllowed,
  adminOnly = false,
  ...rest
}) {
  const auth = useAuth();
  const config = useGlobalConfig();

  return (
    <Route
      {...rest}
      render={(props) => {
        // 1) Pendant init ou silent renew → attendre (pas de redirect)
        if (auth.isLoading || auth.activeNavigator === "signinSilent") {
          return <CenteredLoader />;
        }

        // 2) Une déconnexion est en cours dans un autre onglet → sortir vers une page
        // publique. Sans cette garde, le signinRedirect ci-dessous repart vers Keycloak
        // pendant que le cookie SSO est encore vivant et recrée une session que la
        // déconnexion en cours ne terminera pas.
        if (deconnexionEnCours()) {
          return <Redirect to="/" />;
        }

        // 3) Pas authentifié → lancer le login OIDC (pas de Redirect)
        if (!auth.isAuthenticated) {
          const params = {state: { returnUrl: props.location.pathname + props.location.search }};
          if (config?.SSO && config.SSO !== "false") {
            params.extraQueryParams = { kc_idp_hint: config.SSO };
          }
          auth.signinRedirect(params);
          return null;
        }

        // 4) Auth OK mais infos métier pas prêtes → attendre
        if (!userInfo) {
          return <CenteredLoader />;
        }

        // 5) Règles métier: rôles Keycloak
        const userRoles = userInfo.roles ?? [];
        const isAdminOK = !adminOnly || userRoles.includes("SUPER_ADMIN");
        const isRoleOK = !rolesAllowed || rolesAllowed.some(r => userRoles.includes(r));

        if (!isAdminOK || !isRoleOK) {
          return (
            <Redirect
              to={{ pathname: "/403", state: { from: props.location.pathname } }}
            />
          );
        }

        // 6) OK → rendre la page protégée
        return (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Component {...props} />
          </ErrorBoundary>
        );
      }}
    />
  );
}